import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import {
  registerSessionTimeoutHandler,
  getSessionTimeoutReason,
  clearSessionTimeoutReason,
} from '../utils/sessionTimeout';
import {
  getActivityTracker,
  cleanupActivityTracker,
  clearSessionData as clearActivitySessionData,
  getTimeUntilIdle,
  getTimeUntilAbsoluteTimeout,
  type ActivityTracker,
} from '../utils/activityTracker';
import {
  refreshAccessToken,
  getTokenExpirationTime,
  storeTokens,
  clearTokens,
  TokenRefreshError,
} from '../utils/tokenRefresh';
import { retryAuthOperation } from '../utils/authRetry';

interface User {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  picture?: string;
  'cognito:username'?: string;
  'cognito:groups'?: string[];
  [key: string]: unknown; // Allow other claims
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  idToken: string | null;
  user: User | null;
  login: () => void;
  logout: () => void;
  handleAuthenticationResult: (accessToken: string, idToken: string, userInfo: User) => void;
  hasRole: (role: string) => boolean;
  canAddCampaigns: () => boolean;
  canEditCampaigns: () => boolean;
  canDeleteCampaigns: () => boolean;
  canListCampaigns: () => boolean;
  canViewCampaigns: () => boolean;
  canManageCampaigns: () => boolean;
  canManageUsers: () => boolean; // Added for user management
  handleSessionTimeout: (reason?: string) => void; // Added for session timeout handling
  sessionTimeoutReason: string | null; // Added to track session timeout reason
  // Activity tracking properties
  isSessionIdle: boolean;
  timeUntilIdleTimeout: number;
  timeUntilAbsoluteTimeout: number;
  isRecentlyActive: boolean;
  extendSession: () => void;
  // Token refresh properties
  refreshAccessToken: () => Promise<void>;
  isTokenRefreshInProgress: boolean;
  lastTokenRefreshError: string | null;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  accessToken: null,
  idToken: null,
  user: null,
  login: () => {},
  logout: () => {},
  handleAuthenticationResult: () => {},
  hasRole: () => false,
  canAddCampaigns: () => false,
  canEditCampaigns: () => false,
  canDeleteCampaigns: () => false,
  canListCampaigns: () => false,
  canViewCampaigns: () => false,
  canManageCampaigns: () => false,
  canManageUsers: () => false, // Added for user management
  handleSessionTimeout: () => {}, // Added for session timeout handling
  sessionTimeoutReason: null, // Added to track session timeout reason
  // Activity tracking defaults
  isSessionIdle: false,
  timeUntilIdleTimeout: 0,
  timeUntilAbsoluteTimeout: 0,
  isRecentlyActive: false,
  extendSession: () => {},
  // Token refresh defaults
  refreshAccessToken: async () => {},
  isTokenRefreshInProgress: false,
  lastTokenRefreshError: null,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [sessionTimeoutReason, setSessionTimeoutReason] = useState<string | null>(null);

  // Activity tracking state
  const [activityTracker, setActivityTracker] = useState<ActivityTracker | null>(null);
  const [isSessionIdle, setIsSessionIdle] = useState(false);
  const [timeUntilIdleTimeout, setTimeUntilIdleTimeout] = useState(0);
  const [timeUntilAbsoluteTimeout, setTimeUntilAbsoluteTimeout] = useState(0);
  const [isRecentlyActive, setIsRecentlyActive] = useState(false);

  // Token refresh state
  const [isTokenRefreshInProgress, setIsTokenRefreshInProgress] = useState(false);
  const [lastTokenRefreshError, setLastTokenRefreshError] = useState<string | null>(null);

  useEffect(() => {
    const checkTokens = async () => {
      try {
        const storedAccessToken = sessionStorage.getItem('access_token');
        const storedIdToken = sessionStorage.getItem('id_token');
        const storedUser = sessionStorage.getItem('user');

        if (storedAccessToken && storedIdToken) {
          const parsedUser = storedUser ? JSON.parse(storedUser) : null;
          setAccessToken(storedAccessToken);
          setIdToken(storedIdToken);
          setUser(parsedUser);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        // Clear potentially corrupted session storage items
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('id_token');
        sessionStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };

    checkTokens();
  }, []);
  const login = useCallback(() => {
    const clientId = import.meta.env.REACT_APP_USER_POOL_WEB_CLIENT_ID ?? '';
    const redirectUri = encodeURIComponent(`${window.location.origin}/callback`);

    // Check if we're using LocalStack
    const isLocalStack = import.meta.env.REACT_APP_ENVIRONMENT_TYPE === 'local';

    if (isLocalStack) {
      // Use localstack Cognito endpoint with direct localhost access
      if (!clientId) {
        console.error('Client ID is missing! This will cause the login to fail.');
      }

      const loginUrl = `http://localhost:4566/_aws/cognito-idp/login?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`;
      console.log('Redirecting to:', loginUrl);
      window.location.href = loginUrl;
    } else {
      // Use real AWS Cognito
      const domain_prefix = import.meta.env.REACT_APP_COGNITO_DOMAIN_PREFIX;
      const region = import.meta.env.REACT_APP_REGION;
      window.location.href = `https://${domain_prefix}.auth.${region}.amazoncognito.com/login?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`;
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('user');

    // Clear activity tracking data
    clearActivitySessionData();
    cleanupActivityTracker();

    // Clear tokens using utility
    clearTokens();

    setAccessToken(null);
    setIdToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setSessionTimeoutReason(null);
    setActivityTracker(null);
    setIsTokenRefreshInProgress(false);
    setLastTokenRefreshError(null);
    // Optionally redirect to login page or home page after logout
    // window.location.href = '/login';
  }, []);

  const handleSessionTimeout = useCallback((reason = 'session_expired') => {
    console.warn('🔒 Session timeout handled:', reason);

    // Clear all session data
    sessionStorage.clear();
    setAccessToken(null);
    setIdToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setSessionTimeoutReason(reason);

    // Redirect to login with reason
    window.location.href = `/login?reason=${reason}`;
  }, []);

  const extendSession = useCallback(() => {
    if (activityTracker) {
      activityTracker.trackActivity();
      // Update activity state immediately
      setIsRecentlyActive(true);
      setIsSessionIdle(false);
    }
  }, [activityTracker]);

  const refreshAccessTokenImpl = useCallback(async (): Promise<void> => {
    if (isTokenRefreshInProgress) {
      console.log('Token refresh already in progress, skipping...');
      return;
    }

    if (!accessToken) {
      throw new Error('No access token available for refresh');
    }

    // Check if activity tracker allows refresh
    if (activityTracker) {
      try {
        const tokenExpiry = getTokenExpirationTime(accessToken);
        if (!activityTracker.shouldAllowTokenRefresh(tokenExpiry)) {
          console.log('Token refresh denied by activity tracker - user not sufficiently active');
          handleSessionTimeout('insufficient_activity');
          return;
        }
      } catch (error) {
        console.warn('Unable to check activity for token refresh:', error);
        // Continue with refresh attempt if we can't check activity
      }
    }

    setIsTokenRefreshInProgress(true);
    setLastTokenRefreshError(null);

    try {
      // Use retry mechanism for token refresh
      const retryResult = await retryAuthOperation(() => refreshAccessToken(), 'token-refresh');

      if (!retryResult.success || !retryResult.result) {
        throw retryResult.error ?? new Error('Token refresh failed with retry mechanism');
      }

      const tokenResponse = retryResult.result;

      // Store new tokens
      storeTokens(tokenResponse);

      // Update state
      setAccessToken(tokenResponse.access_token);
      setIdToken(tokenResponse.id_token);

      // Parse user info from new ID token if needed
      try {
        const idTokenParts = tokenResponse.id_token.split('.');
        if (idTokenParts.length === 3) {
          const payload = JSON.parse(atob(idTokenParts[1]));
          setUser(payload);
          sessionStorage.setItem('user', JSON.stringify(payload));
        }
      } catch (error) {
        console.warn('Unable to parse user info from refreshed ID token:', error);
      }

      console.log(`Token refresh successful after ${retryResult.attempts.length} attempts`);
    } catch (error) {
      const errorMessage =
        error instanceof TokenRefreshError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Unknown token refresh error';

      console.error('Token refresh failed after retries:', errorMessage);
      setLastTokenRefreshError(errorMessage);

      // If refresh fails, log out the user
      if (error instanceof TokenRefreshError && error.statusCode === 401) {
        handleSessionTimeout('refresh_token_expired');
      } else {
        handleSessionTimeout('token_refresh_failed');
      }
    } finally {
      setIsTokenRefreshInProgress(false);
    }
  }, [isTokenRefreshInProgress, accessToken, activityTracker, handleSessionTimeout]);

  const handleAuthenticationResult = useCallback(
    (newAccessToken: string, newIdToken: string, newUserInfo: User) => {
      sessionStorage.setItem('access_token', newAccessToken);
      sessionStorage.setItem('id_token', newIdToken);
      sessionStorage.setItem('user', JSON.stringify(newUserInfo));

      setAccessToken(newAccessToken);
      setIdToken(newIdToken);
      setUser(newUserInfo);
      setIsAuthenticated(true);
      setIsLoading(false);
    },
    []
  );

  const hasRole = useCallback(
    (role: string): boolean => {
      return user?.['cognito:groups']?.includes(role) ?? false;
    },
    [user]
  );

  const canAddCampaigns = useCallback((): boolean => {
    return hasRole('CampaignManagers') || hasRole('SystemAdministrators');
  }, [hasRole]);

  const canEditCampaigns = useCallback((): boolean => {
    return hasRole('CampaignManagers') || hasRole('SystemAdministrators');
  }, [hasRole]);

  const canDeleteCampaigns = useCallback((): boolean => {
    return hasRole('CampaignManagers') || hasRole('SystemAdministrators');
  }, [hasRole]);

  const canListCampaigns = useCallback((): boolean => {
    // Everyone with access to the system can list campaigns
    return true;
  }, []);

  const canViewCampaigns = useCallback((): boolean => {
    // Everyone with access to the system can view campaign details
    return true;
  }, []);

  // Keep canManageCampaigns for backward compatibility
  const canManageCampaigns = useCallback((): boolean => {
    return canAddCampaigns() || canEditCampaigns() || canDeleteCampaigns();
  }, [canAddCampaigns, canEditCampaigns, canDeleteCampaigns]);

  const canManageUsers = useCallback((): boolean => {
    return hasRole('SystemAdministrators');
  }, [hasRole]);

  // Register session timeout handler and check for session timeout reasons
  useEffect(() => {
    // Register this component's session timeout handler
    registerSessionTimeoutHandler({ handleSessionTimeout });

    // Check if we were redirected due to session timeout
    const timeoutReason = getSessionTimeoutReason();
    if (timeoutReason) {
      setSessionTimeoutReason(timeoutReason);
      clearSessionTimeoutReason();
    }
  }, [handleSessionTimeout]);

  // Initialize activity tracker when authenticated
  useEffect(() => {
    if (isAuthenticated && !activityTracker) {
      const tracker = getActivityTracker();
      setActivityTracker(tracker);
    } else if (!isAuthenticated && activityTracker) {
      cleanupActivityTracker();
      setActivityTracker(null);
    }
  }, [isAuthenticated, activityTracker]);

  // Update activity tracking state periodically
  useEffect(() => {
    if (!activityTracker || !isAuthenticated) {
      return;
    }

    const updateActivityState = () => {
      const idle = !activityTracker.isWithinIdleThreshold();
      const recentlyActive = activityTracker.isRecentlyActive();
      const timeUntilIdle = getTimeUntilIdle();
      const timeUntilAbsolute = getTimeUntilAbsoluteTimeout();

      setIsSessionIdle(idle);
      setIsRecentlyActive(recentlyActive);
      setTimeUntilIdleTimeout(timeUntilIdle);
      setTimeUntilAbsoluteTimeout(timeUntilAbsolute);

      // Auto-logout if session has reached absolute limit
      if (timeUntilAbsolute <= 0) {
        handleSessionTimeout('absolute_session_limit');
        return;
      }

      // Auto-logout if user has been idle too long
      if (idle) {
        handleSessionTimeout('idle_timeout');
        return;
      }
    };

    // Update immediately
    updateActivityState();

    // Update every 30 seconds
    const interval = setInterval(updateActivityState, 30000);

    return () => clearInterval(interval);
  }, [activityTracker, isAuthenticated, handleSessionTimeout]);

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      accessToken,
      idToken,
      user,
      login,
      logout,
      handleAuthenticationResult,
      hasRole,
      canAddCampaigns,
      canEditCampaigns,
      canDeleteCampaigns,
      canListCampaigns,
      canViewCampaigns,
      canManageCampaigns,
      canManageUsers, // Added for user management
      handleSessionTimeout, // Added for session timeout handling
      sessionTimeoutReason, // Added to track session timeout reason
      // Activity tracking properties
      isSessionIdle,
      timeUntilIdleTimeout,
      timeUntilAbsoluteTimeout,
      isRecentlyActive,
      extendSession,
      // Token refresh properties
      refreshAccessToken: refreshAccessTokenImpl,
      isTokenRefreshInProgress,
      lastTokenRefreshError,
    }),
    [
      isAuthenticated,
      isLoading,
      accessToken,
      idToken,
      user,
      login,
      logout,
      handleAuthenticationResult,
      hasRole,
      canAddCampaigns,
      canEditCampaigns,
      canDeleteCampaigns,
      canListCampaigns,
      canViewCampaigns,
      canManageCampaigns,
      canManageUsers, // Added for user management
      handleSessionTimeout, // Added for session timeout handling
      sessionTimeoutReason, // Added to track session timeout reason
      // Activity tracking dependencies
      isSessionIdle,
      timeUntilIdleTimeout,
      timeUntilAbsoluteTimeout,
      isRecentlyActive,
      extendSession,
      // Token refresh dependencies
      refreshAccessTokenImpl,
      isTokenRefreshInProgress,
      lastTokenRefreshError,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);
